/** Expression-rule (type=1) editor: DTL field/value + comparison operator.
 *
 * Backend semantics (`Notification_Rule.match_expr`): both `field` and `value`
 * are rendered as Django templates against the notification context, then the
 * rendered STRINGS are compared per `op`. The syntax help spells out the two
 * traps (float coercion on numeric ops, non-empty-string truthiness).
 */
import { Box, MenuItem, Stack, TextField, Typography } from '@mui/material';

import { FieldHelp } from '@/components/FieldHelp';
import { TemplateCodeEditor } from '@/components/TemplateCodeEditor/TemplateCodeEditor';
import type { ContextSchemaSource } from '@/components/TemplateCodeEditor/types';

import { RULES_HELP } from '../fieldHelp';
import { EXPR_OPS, exprOpNeedsValue } from './ruleCatalog';
import type { RuleDraft } from './ruleTypes';

interface Props {
  rule: RuleDraft;
  onRuleChange: (patch: Partial<RuleDraft>) => void;
  disabled?: boolean;
  /** Event context schemas → variable completions (notification editor only;
   *  a ruleset serves many events, so there it stays undefined). */
  contextSchemas?: ContextSchemaSource[];
}

function TemplateField({
  label,
  help,
  value,
  onChange,
  disabled,
  contextSchemas,
  placeholder,
}: {
  label: string;
  help: React.ComponentProps<typeof FieldHelp>['help'];
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  contextSchemas?: ContextSchemaSource[];
  placeholder?: string;
}) {
  return (
    <Box sx={{ flex: 1, minWidth: 260 }}>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
        <FieldHelp help={help} />
      </Stack>
      <TemplateCodeEditor
        value={value}
        onChange={onChange}
        language="django"
        baseLanguage="text"
        readOnly={disabled}
        autoGrow
        maxHeight={120}
        placeholder={placeholder}
        {...(contextSchemas ? { contextSchemas } : {})}
      />
    </Box>
  );
}

export function ExprRuleEditor({ rule, onRuleChange, disabled, contextSchemas }: Props) {
  const needsValue = exprOpNeedsValue(rule.op);
  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start" flexWrap="wrap" useFlexGap>
        <TemplateField
          label="Field (Django template)"
          help={RULES_HELP.exprField}
          value={rule.field ?? ''}
          onChange={(v) => onRuleChange({ field: v })}
          disabled={disabled}
          contextSchemas={contextSchemas}
          placeholder="{{ participant.pax_count }}"
        />
        <Box sx={{ pt: 2.5 }}>
          <TextField
            select
            size="small"
            label="Operator"
            sx={{ width: 250 }}
            value={rule.op}
            disabled={disabled}
            InputProps={{ endAdornment: <FieldHelp help={RULES_HELP.exprOp} /> }}
            onChange={(e) => onRuleChange({ op: Number(e.target.value) })}
          >
            {EXPR_OPS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        {needsValue && (
          <TemplateField
            label="Value (template or constant)"
            help={RULES_HELP.exprValue}
            value={rule.value ?? ''}
            onChange={(v) => onRuleChange({ value: v })}
            disabled={disabled}
            contextSchemas={contextSchemas}
            placeholder="10"
          />
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Both sides render as Django templates against the notification context, then the rendered
        strings are compared. Numeric operators fail the rule when either side is not a number.
      </Typography>
    </Stack>
  );
}
